'use client';

// Avatar animé de qui a ENREGISTRÉ la voix (migration 0675) : rond, cadre bleu clair,
// en haut à droite de l'animation pendant une scène qu'il a dite. 8 images
// (0 = bouche fermée, 1..7 = ouverte) toutes chargées d'avance et empilées :
// changer de bouche ne recharge rien. La bouche suit `mouthFrameAt` (mots
// horodatés de la voix), donc le même rendu dans l'app et dans la vidéo.
// Décoratif : `aria-hidden` (les sous-titres et le générique disent qui parle).

import { useEffect, useRef, useState } from 'react';
import {
  documentSpeakers,
  JRAF_PALETTE,
  mouthFrameAt,
  SPEAKER_FRAME_COUNT,
  speakerRoleCaption,
  speakerRoles,
} from '@jeraisonne/animation';
import { cn } from '../../lib/utils';

/** Prénom + 8 URL d'images (0 = bouche fermée) d'un compte qui parle. */
export interface SpeakerAvatarInfo {
  firstName: string | null;
  frames: readonly string[];
}

/** Avatars connus, par compte (`speakerId` des voix enregistrées). */
export type SpeakerAvatars = Readonly<Record<string, SpeakerAvatarInfo>>;

/** Avatars utilisables : exactement 8 images. */
export function usableAvatar(
  avatars: SpeakerAvatars | null | undefined,
  id: string | null | undefined,
): SpeakerAvatarInfo | null {
  if (!avatars || !id) return null;
  const a = avatars[id];
  return a && a.frames.length === SPEAKER_FRAME_COUNT ? a : null;
}

/** Comparaison de prénoms et de rôles : sans accents ni casse. */
function nameKey(s: string): string {
  return s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().trim();
}

/**
 * Intro et générique : tous les comptes qui ont enregistré une voix utilisée
 * et qui ont un avatar, dans l'ordre d'apparition (3 au plus, côte à côte) ;
 * en dialogue, le rôle tenu dessous — seulement s'il apprend quelque chose :
 * un seul rôle, qui n'est pas le prénom de la personne, et différent de celui
 * des autres avatars (sinon « Christelle et Philippe » sous chaque avatar).
 */
export function bookendHosts(
  doc: Parameters<typeof documentSpeakers>[0],
  avatars: SpeakerAvatars | null | undefined,
): { firstName: string | null; frames: readonly string[]; caption: string | null }[] {
  const roles = speakerRoles(doc);
  const out: { firstName: string | null; frames: readonly string[]; caption: string | null }[] = [];
  for (const id of documentSpeakers(doc)) {
    const a = usableAvatar(avatars, id);
    if (!a) continue;
    // plusieurs rôles : la légende les énumère et ne dit plus rien de cette personne
    const caption = (roles[id]?.length ?? 0) === 1 ? speakerRoleCaption(doc, id) : null;
    out.push({ ...a, caption });
    if (out.length === 3) break;
  }
  const keys = out.map((h) => (h.caption ? nameKey(h.caption) : null));
  return out.map((h, i) => {
    const key = keys[i];
    const useful =
      key !== null &&
      key !== nameKey(h.firstName ?? '') &&
      keys.filter((k) => k === key).length === 1;
    return useful ? h : { ...h, caption: null };
  });
}

export interface SpeakerAvatarProps {
  frames: readonly string[];
  /** image affichée (0 = bouche fermée) */
  frame: number;
  /** diamètre (px du canevas) */
  size?: number;
  className?: string;
}

export function SpeakerAvatar({ frames, frame, size = 64, className }: SpeakerAvatarProps) {
  const shown = frame >= 0 && frame < frames.length ? frame : 0;
  return (
    <div
      aria-hidden
      data-speaker-avatar=""
      data-mouth={shown}
      className={cn('relative shrink-0 overflow-hidden rounded-full', className)}
      style={{
        width: size,
        height: size,
        backgroundColor: JRAF_PALETTE.night,
        boxShadow: `0 0 0 3px ${JRAF_PALETTE['primary-light']}, 0 6px 18px rgba(0,0,0,0.35)`,
      }}
    >
      {frames.map((src, i) => (
        <img
          key={src}
          src={src}
          alt=""
          draggable={false}
          className="absolute inset-0 h-full w-full object-cover object-top"
          style={{ opacity: i === shown ? 1 : 0 }}
        />
      ))}
    </div>
  );
}

export interface LiveSpeakerAvatarProps {
  frames: readonly string[];
  /** mots de la voix, datés depuis le début du fichier */
  words?: readonly { startMs: number; endMs: number }[] | undefined;
  /** instant de la voix dans le fichier, lu à chaque image ; null : elle ne joue pas */
  voiceTime: () => { fileMs: number; playing: boolean } | null;
  /** mouvements réduits : bouche fermée */
  still?: boolean;
  size?: number;
  className?: string;
}

/** Avatar du lecteur : la bouche suit la voix qui joue (boucle d'images). */
export function LiveSpeakerAvatar({
  frames,
  words,
  voiceTime,
  still = false,
  size,
  className,
}: LiveSpeakerAvatarProps) {
  const [frame, setFrame] = useState(0);
  const read = useRef(voiceTime);
  read.current = voiceTime;

  useEffect(() => {
    if (still || typeof requestAnimationFrame !== 'function') {
      setFrame(0);
      return;
    }
    let id = 0;
    let last = -1;
    const tick = () => {
      const v = read.current();
      const f = v ? mouthFrameAt(words, v.fileMs, v.playing) : 0;
      if (f !== last) {
        last = f;
        setFrame(f);
      }
      id = requestAnimationFrame(tick);
    };
    id = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(id);
  }, [words, still]);

  return <SpeakerAvatar frames={frames} frame={frame} size={size} className={className} />;
}
